import {
  AvailableFeedbackSteps,
  FeedbackWidgetInputs,
  FeedbackWidgetNamespace,
  FeedbackWidgetStepConfig,
  GenericFeedbackWidgetProps
} from 'components/surveys/generic-feedback-widget/types.ts'
import { BoltSurveyType, SurveyAnswer, SurveyRequestBody } from 'components/surveys/types.ts'
import { SalesFeedbackWidgetStep } from 'components/surveys/sales-survey/types.ts'
import { EXTRA_INPUT_KEY } from 'components/surveys/generic-feedback-widget/constants.ts'
import i18n, { keyFromSelector, type SelectorKey, type SelectorParam } from 'i18next'

/**
 * Builds a namespaced translation key for a feedback widget config from a type-checked selector.
 * @param {FeedbackWidgetNamespace} ns
 * @param {SelectorParam<Ns>} selector
 * @returns {SelectorKey}
 */
export const getFeedbackWidgetKey = <Ns extends FeedbackWidgetNamespace>(ns: Ns, selector: SelectorParam<Ns>): SelectorKey =>
  `${ns}:${keyFromSelector(selector, { ns })}` as SelectorKey

/**
 * Calculates the score for a specific step based on its configuration and input.
 * @param {GenericFeedbackWidgetProps<S>["config"][S]} config
 * @param {FeedbackWidgetInputs<S>[S]} input
 * @returns {number}
 */
const getScoreFromConfigByInput = <S extends AvailableFeedbackSteps>(
  config: GenericFeedbackWidgetProps<S>['config'][S],
  input: FeedbackWidgetInputs<S>[S]
): number => {
  // If no weight is defined, no score is calculated
  if (!config.weight) return 0

  switch (config.type) {
    case 'rating': {
      return typeof input === 'number' ? input * config.weight : 0
    }

    case 'radio': {
      const option = config.options?.find((o) => o.value === input)
      return option?.score ? option.score * config.weight : 0
    }

    case 'checkbox': {
      if (Array.isArray(input)) {
        // If the input is an array, sum the scores of all selected options
        return input.reduce((total, value) => {
          if (!config.weight) return total

          const option = config.options?.find((o) => o.value === value)
          return total + (option?.score ? option.score * config.weight : 0)
        }, 0)
      }
      return 0
    }

    default:
      return 0
  }
}

/**
 * Calculates the total score based on the provided configuration and inputs.
 * @param {GenericFeedbackWidgetProps<S>["config"]} config
 * @param {FeedbackWidgetInputs<S>} inputs
 * @returns {number}
 */
const calculateTotalScore = <S extends AvailableFeedbackSteps>(
  config: GenericFeedbackWidgetProps<S>['config'],
  inputs: FeedbackWidgetInputs<S>
): number => {
  return Object.keys(config).reduce((prev, curr) => {
    const step = curr as S
    const currConfig = config[step]
    const currInput = inputs[step]

    const score = getScoreFromConfigByInput(currConfig, currInput)

    return prev + Math.round(score * 10) / 10 // Round to one decimal place
  }, 0)
}

/**
 * Returns the answer object based on the step type and answer value
 * @param {FeedbackWidgetStepConfig["type"]} type
 * @param answerValue
 * @returns {SurveyAnswer}
 */
const getAnswerBasedOnStepType = (type: FeedbackWidgetStepConfig['type'], answerValue: unknown): SurveyAnswer => {
  if (!answerValue) return {}

  switch (type) {
    case 'rating':
      return { number: answerValue as number }
    case 'radio':
      return { text: answerValue as string }
    case 'checkbox':
      return { text: Array.isArray(answerValue) ? answerValue.join(', ') : (answerValue as string) }
    case 'textArea':
      return { text: answerValue as string }
  }
}

/**
 * Maps the widget inputs to the survey payload format
 * @template S
 * @param {FeedbackWidgetInputs<S>} inputs
 * @param {SalesFeedbackWidgetStep[]} order
 * @param {GenericFeedbackWidgetProps<S>["config"]} config
 * @param {BoltSurveyType} surveyType
 * @returns {SurveyRequestBody}
 */
export const mapWidgetInputsToSurveyPayload = <S extends AvailableFeedbackSteps>(
  inputs: FeedbackWidgetInputs<S>,
  order: SalesFeedbackWidgetStep[],
  config: GenericFeedbackWidgetProps<S>['config'],
  surveyType: BoltSurveyType
): SurveyRequestBody => {
  return {
    surveyType,
    totalScore: calculateTotalScore(config, inputs),
    questions: order.map((step, index) => {
      const typedStep = step as S
      const { question, type, options } = config[typedStep]
      let value: unknown = inputs[typedStep]

      // Convert the value to the associated label
      if (type === 'radio') {
        const valueLabel = options?.find((o) => o.value === inputs[typedStep])?.label
        if (valueLabel) {
          value = i18n.t(valueLabel)
        }
      }

      // Re-map values from the checkbox inputs to the associated label
      if (type === 'checkbox' && Array.isArray(value)) {
        value = value.map((v) => {
          const label = options?.find((o) => o.value === v)?.label
          return label ? i18n.t(label) : v
        })
      }

      const answer = getAnswerBasedOnStepType(type, value)

      if (options?.some((o) => o.triggerExtraInput) && inputs?.[EXTRA_INPUT_KEY]?.[typedStep]) {
        answer.other = inputs[EXTRA_INPUT_KEY][typedStep] as string
      }

      return {
        order: index + 1,
        text: i18n.t(question),
        answer
      }
    })
  }
}
